'use client';

import dayjs from 'dayjs';
import type { PropsWithChildren } from 'react';
import { createContext, useContext, useEffect, useState } from 'react';

import type { AreaCode, TrendApiPayload } from '@/types/market';

const areaCodeContext = createContext<AreaCode>('0');
const onChangeAreaCodeContext = createContext<(areaCode: AreaCode) => void>(() => {});
const dataContext = createContext<Record<'0' | '1' | '9', TrendApiPayload> | null | undefined>(null);

export function useAreaCode() {
	return useContext(areaCodeContext);
}

export function useData() {
	return useContext(dataContext);
}

export function useMarketContext() {
	return {
		areaCode: useContext(areaCodeContext),
		onChangeAreaCode: useContext(onChangeAreaCodeContext),
		data: useContext(dataContext),
	};
}

export default function MarketProvider({ children }: PropsWithChildren) {
	const [data, setData] = useState<Record<'0' | '1' | '9', TrendApiPayload>>();
	const [areaCode, setAreaCode] = useState<AreaCode>('0');

	useEffect(() => {
		const from = dayjs().subtract(1, 'year').format('YYYY-MM');
		const to = dayjs().format('YYYY-MM');
		if (!data) {
			Promise.all(
				['0', '1', '9'].map(async (areaCode) => {
					const response = await fetch(`/api/trend?areaCode=${areaCode}&from=${from}&to=${to}`, { cache: 'no-cache' });
					if (response.ok) {
						return response.json() as Promise<TrendApiPayload>;
					}
					return {
						areaCode: areaCode as AreaCode,
						from,
						to,
						smpMonthly: [],
						recMonthly: [],
					};
				})
			).then(([data_0, data_1, data_9]) => {
				setData({
					'0': data_0,
					'1': data_1,
					'9': data_9,
				});
			});
		}
	}, [data]);

	return (
		<areaCodeContext.Provider value={areaCode}>
			<onChangeAreaCodeContext.Provider
				value={(value) => {
					setAreaCode(value);
				}}
			>
				<dataContext.Provider value={data}>{children}</dataContext.Provider>
			</onChangeAreaCodeContext.Provider>
		</areaCodeContext.Provider>
	);
}
